import { useProjectParams } from '@hooks/useProjectParams';
import type { BimWorkOrderChangeQuery, Maybe, ProjectData } from 'gql/graphql';
import { ContractStatusEnum, ProjectStatusEnum, WorkOrderChangeStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { BimWorkOrderChangeCreateOrEdit } from 'pages/Project/BimProject/WorkOrderChangeCreateOrEdit';
import { Button, cnMixSpace } from 'uikit';

import type { ForwardedRef } from 'react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const EditStructureButtonStyle = { marginRight: '16px' };

interface IBimOrderChangeRequisites {
    contractStatus: ContractStatusEnum | undefined;
    isFrameContract: boolean;
    isLoading: boolean;
    onSuccess: () => void;
    projectData: ProjectData | undefined | null;
    refreshProject: () => void;
    refreshWorkOrderChangeData: () => void;
    orderRules?: IUseAccess['orderBim'];
    statusProject?: ProjectStatusEnum;
    workOrderChangeData: Maybe<BimWorkOrderChangeQuery['bimWorkOrderChange']['data'] | undefined>;
    workOrderStatus?: WorkOrderStatusEnum;
    statusWorkOrderChange?: WorkOrderChangeStatusEnum;
}

export const BimOrderChangeRequisites = forwardRef(
    (
        {
            contractStatus,
            isFrameContract,
            isLoading,
            onSuccess,
            projectData,
            refreshProject,
            refreshWorkOrderChangeData,
            workOrderChangeData,
            statusProject,
            orderRules,
            workOrderStatus,
            statusWorkOrderChange,
        }: IBimOrderChangeRequisites,
        ref: ForwardedRef<{ openModalFromOutside: () => void }>,
    ) => {
        const [isModalOpened, setIsModalOpened] = useState<boolean>(false);
        const { id: projectId, orderChangeId, methodology } = useProjectParams();

        const navigate = useNavigate();

        const handleOnEditButtonClick = useCallback(() => {
            setIsModalOpened(true);
        }, []);

        const handleOnEditButtonClickFromOutSide = useCallback(() => {
            setTimeout(() => {
                setIsModalOpened(true);
            }, 100);
        }, []);

        const handleOnEditCancel = useCallback(() => {
            setIsModalOpened(false);
        }, []);

        useImperativeHandle(ref, () => ({
            openModalFromOutside: handleOnEditButtonClickFromOutSide,
        }));

        const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
        const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;

        const isWorkOrderStatusCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;
        const isWorkOrderStatusDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
        const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

        const isStatusWorkOrderChangeOnFormation = statusWorkOrderChange === WorkOrderChangeStatusEnum.OnFormation;

        const handleStructureChange = useCallback(
            () => navigate(`/projects/${methodology}/${projectId}/orderChange/${orderChangeId}/structure`),
            [methodology, navigate, orderChangeId, projectId],
        );

        const isDisabledButtonChangeStructure =
            isContractStatusDelayed ||
            isContractStatusCompleted ||
            isWorkOrderStatusCompleted ||
            isWorkOrderStatusDelayed ||
            isProjectStatusDelayed ||
            !orderRules?.canEditStructure ||
            !isStatusWorkOrderChangeOnFormation;

        return (
            <>
                <Button
                    label="Редактировать данные"
                    size="s"
                    view="secondary"
                    className={cnMixSpace({ mR: 'xs' })}
                    onClick={handleOnEditButtonClick}
                    loading={isLoading}
                    disabled={!orderRules?.canEdit || isProjectStatusDelayed}
                />
                <Button
                    label="Редактировать структуру"
                    size="s"
                    view="secondary"
                    style={EditStructureButtonStyle}
                    disabled={isDisabledButtonChangeStructure}
                    onClick={handleStructureChange}
                />

                {isModalOpened && workOrderChangeData?.workOrderId && (
                    <BimWorkOrderChangeCreateOrEdit
                        contractStatus={contractStatus}
                        isFrameContract={isFrameContract}
                        onClose={handleOnEditCancel}
                        onSuccess={onSuccess}
                        projectData={projectData}
                        refreshProjectData={refreshProject}
                        refreshWorkOrderChangeData={refreshWorkOrderChangeData}
                        workOrderChangeData={workOrderChangeData}
                        workOrderId={workOrderChangeData.workOrderId}
                        statusWorkOrderChange={statusWorkOrderChange}
                    />
                )}
            </>
        );
    },
);

BimOrderChangeRequisites.displayName = 'BimOrderChangeRequisites';
